import { Common, color } from '@stockbit/ui';
import { useRouter } from 'next/router';
import {
  virtualTradingAction,
  VIRTUAL_TRADING_TYPES,
} from 'lib/analytics/virtualTrading';
import { ORDER_TYPE } from 'constants/trading';
import { formatNumber } from 'utils/number';
import useTradingAccount from 'hooks/useTradingAccount';
import tradingDataStore from 'lib/stores/tradingData';
import useAuthModalStore from 'features/securities/AuthModal/store';
import { AUTH_ORDER_TYPE } from 'features/securities/AuthModal/constants';
import { getBuyLot } from 'global/VirtualTrading/utils/formula';
import { useVirtualOrderStore } from 'global/VirtualTrading/stores/useVirtualOrderStore';
import FormItem from '../components/FormItem';
import OverLimitText from '../components/OverLimitText';
import generateStepPrice from '../utils/generateStepPrice';
import { lotSize } from '../constant';

import { ButtonWrapper, SwitchWrapper, OrderButton } from '../styled';
import virtualTradingStore from './store';
import NumberInput from '../components/NumberInput';

const { Text, IconWrapper, Iconmoon } = Common;

interface iFormBuy {
  orderType: string;
  orderid?: string;
  closeModal: () => void;
}

function FormBuy({ orderType, orderid, closeModal }: iFormBuy) {
  const router = useRouter();
  const { isRealTrading } = useTradingAccount();
  const { portfolio, getOrderHistory, getPortfolio } = tradingDataStore();
  const showAuthModal = useAuthModalStore((state) => state.showModal);

  const isLoading = useVirtualOrderStore((s) => s.isLoading);
  const createVirtualOrder = useVirtualOrderStore((s) => s.createVirtualOrder);
  const createVirtualAmendOrder = useVirtualOrderStore(
    (s) => s.createVirtualAmendOrder,
  );

  const {
    data: { tradingbalance },
  } = portfolio;

  const { price, shares, stockCode, setPrice, setShares } =
    virtualTradingStore();

  const amount = getBuyLot(shares / lotSize, Number(price));

  const onSubmitOrder = async () => {
    try {
      if (orderType === ORDER_TYPE.VIRTUAL_AMEND_BUY) {
        await createVirtualAmendOrder({
          gtc: false,
          order_id: orderid,
          price: Number(price),
          shares: Number(shares),
          symbol: stockCode,
        });
      }

      if (orderType === ORDER_TYPE.VIRTUAL_BUY) {
        await createVirtualOrder('buy', stockCode, {
          gtc: false,
          price: Number(price),
          shares: Number(shares),
          tradeshare: false,
        });
      }

      getOrderHistory(false);
      getPortfolio(false);
      closeModal();

      if (orderType === ORDER_TYPE.VIRTUAL_BUY) {
        router.push('/trade/order');
      }
    } catch (error) {
      Promise.reject(error);
    }
  };

  const onClickSwitchVirtual = () => {
    const isVirtualBuy = orderType === ORDER_TYPE.VIRTUAL_BUY;

    closeModal();

    if (isVirtualBuy) {
      showAuthModal({
        symbol: stockCode,
        orderType: AUTH_ORDER_TYPE.BUY,
      });
    }
  };

  const isOverlimit = price * shares > tradingbalance;
  const disableBuy = shares < 100 || isOverlimit;

  return (
    <>
      <FormItem label="Trading Balance">
        <Text family="bold">
          {formatNumber(
            tradingbalance,
            isRealTrading,
            isOverlimit ? '0.00' : '0,0',
          )}
        </Text>
      </FormItem>

      <FormItem label="Amount">
        <Text>{formatNumber(amount, false)}</Text>
      </FormItem>

      <FormItem label="Price">
        <NumberInput
          value={price}
          min={0}
          step={generateStepPrice(price)}
          onChange={(newPrice) => setPrice(newPrice)}
        />
      </FormItem>

      <FormItem label="Buy Order Lot">
        <NumberInput
          defaultValue={0}
          min={0}
          value={shares / lotSize}
          step={1}
          onChange={(lot) => {
            setShares(lot * lotSize);
          }}
        />
      </FormItem>
      {isOverlimit && <OverLimitText isRight={false} />}

      <SwitchWrapper onClick={onClickSwitchVirtual} isOverlimit={isOverlimit}>
        <Text align="center" color="#00ab6b" mr="5px">
          Switch to Real Account
        </Text>
        <IconWrapper size="18px">
          <Iconmoon
            className="icon-tradesmall_switch"
            size="12px"
            color={color.primary}
          />
        </IconWrapper>
      </SwitchWrapper>

      <ButtonWrapper>
        <OrderButton
          disabled={disableBuy}
          onClick={() => {
            onSubmitOrder();

            virtualTradingAction.onBuyOrderPlaced({
              Symbol: stockCode,
              Price: price,
              Lot: shares / lotSize,
              Type: VIRTUAL_TRADING_TYPES.virtualTrading,
            });
          }}
          loading={isLoading}
          type="primary"
          block
        >
          <Text>Place Order</Text>
        </OrderButton>
      </ButtonWrapper>
    </>
  );
}

export default FormBuy;
